import type {
    ArchiveMemberRequest,
    CancelMembershipRequest,
    ConvertTrialToPaidRequest,
    CreateMemberLinkRequest,
    EditLimitedProfileFamilyMemberRequest,
    EditMemberContactDetailsRequest,
    EditMemberContributionsRequest,
    EditMemberDiningDetailsRequest,
    EditMemberEventsDetailsRequest,
    EditMemberFamilyDetailsRequest,
    EditMemberPaymentDetailsRequest,
    EditMemberPersonalInfoRequest,
    EditMemberShoppingDetailsRequest,
    EditMemberTravelDetailsRequest,
    EditMemberWellnessDetailsRequest,
    ExtendTrialRequest,
    LinkFamilyMemberProfileRequest,
    LinkLimitedProfileFamilyMemberRequest,
    ManageMemberRequest,
    UnlinkFamilyMemberProfileRequest,
    UpdateMemberLinkRequest,
    UpdateMemberMembershipRequest,
} from "@albertine/types/openapi/lmt";
import { createContext, use } from "react";
import type { AgentPickerItem } from "../agent-picker/AgentPicker";
import type {
    LinkableMember,
    MembershipOption,
    Profile,
    ProfileGroup,
    StripeCustomer,
} from "./data/profile";
import type { ProfileOverlay } from "./overlays/profileOverlay";

/** Same shape as production `CreateReferralPromotionSchema` (not in OpenAPI). */
export type ReferralPromotionRequest = {
    popupContent: { description: string };
    notificationContent: { description: string };
};

export type MemberProfileStore = {
    profile: Profile;
    overlay: ProfileOverlay | null;
    setOverlay: (overlay: ProfileOverlay | null) => void;
    agents: AgentPickerItem[];
    groups: ProfileGroup[];
    members: LinkableMember[];
    membershipTiers: readonly MembershipOption[];
    isAdmin: boolean;
    saving: boolean;
    openProactivityMessage: (messageId: string) => void;
    findStripeCustomer: (stripeCustomerId: string) => Promise<StripeCustomer | null>;
    saveManage: (payload: ManageMemberRequest, onSuccess: () => void) => void;
    savePersonal: (
        payload: EditMemberPersonalInfoRequest,
        onSuccess: () => void,
    ) => void;
    saveContact: (
        payload: EditMemberContactDetailsRequest,
        onSuccess: () => void,
    ) => void;
    saveAdmin: (payload: EditMemberContributionsRequest, onSuccess: () => void) => void;
    saveFamilyNotes: (
        payload: EditMemberFamilyDetailsRequest,
        onSuccess: () => void,
    ) => void;
    createLimitedFamily: (
        payload: LinkLimitedProfileFamilyMemberRequest,
        onSuccess: () => void,
    ) => void;
    editLimitedFamily: (
        familyMemberId: string,
        payload: EditLimitedProfileFamilyMemberRequest,
        onSuccess: () => void,
    ) => void;
    deleteLimitedFamily: (familyMemberId: string, onSuccess: () => void) => void;
    createLinkedFamily: (
        payload: LinkFamilyMemberProfileRequest,
        onSuccess: () => void,
    ) => void;
    replaceLinkedFamily: (
        memberIdToUnlink: string,
        payload: LinkFamilyMemberProfileRequest,
        onSuccess: () => void,
    ) => void;
    deleteLinkedFamily: (
        payload: UnlinkFamilyMemberProfileRequest,
        onSuccess: () => void,
    ) => void;
    saveDining: (
        payload: EditMemberDiningDetailsRequest,
        onSuccess: () => void,
    ) => void;
    saveTravel: (
        payload: EditMemberTravelDetailsRequest,
        onSuccess: () => void,
    ) => void;
    saveEvents: (
        payload: EditMemberEventsDetailsRequest,
        onSuccess: () => void,
    ) => void;
    saveWellness: (
        payload: EditMemberWellnessDetailsRequest,
        onSuccess: () => void,
    ) => void;
    saveShopping: (
        payload: EditMemberShoppingDetailsRequest,
        onSuccess: () => void,
    ) => void;
    savePayments: (
        payload: EditMemberPaymentDetailsRequest,
        onSuccess: () => void,
    ) => void;
    createLink: (payload: CreateMemberLinkRequest, onSuccess: () => void) => void;
    updateLink: (
        linkId: string,
        payload: UpdateMemberLinkRequest,
        onSuccess: () => void,
    ) => void;
    deleteLink: (linkId: string, onSuccess: () => void) => void;
    archive: (payload: ArchiveMemberRequest, onSuccess: () => void) => void;
    activate: (onSuccess: () => void) => void;
    extendTrial: (payload: ExtendTrialRequest, onSuccess: () => void) => void;
    upgradeMembership: (
        payload: ConvertTrialToPaidRequest,
        onSuccess: () => void,
    ) => void;
    saveMembership: (
        payload: UpdateMemberMembershipRequest,
        onSuccess: () => void,
    ) => void;
    endMembership: (payload: CancelMembershipRequest, onSuccess: () => void) => void;
    createReferral: (payload: ReferralPromotionRequest, onSuccess: () => void) => void;
    editReferral: (
        promotionId: string,
        payload: ReferralPromotionRequest,
        onSuccess: () => void,
    ) => void;
    setOnboardingByPhone: (enable: boolean, onSuccess: () => void) => void;
};

export const MemberProfileContext = createContext<MemberProfileStore | null>(null);

export function useMemberProfile() {
    const value = use(MemberProfileContext);
    if (!value) {
        throw new Error("useMemberProfile requires a MemberProfile provider");
    }
    return value;
}
